import { AppSettings } from "@/AppSettings";
import { ConfigurationStatus, CoordinatingGroup, Institution, MasterGroup, Participant, Trial } from "@/api/dtos";
import Multiselect, { IMultiselectProps } from "@/components/forms/inputs/Multiselect";
import Text, { ITextProps } from "@/components/forms/inputs/Text";
import CoordinatingGroupsContext from "@/contexts/data/CoordinatingGroupsContext";
import InstitutionContext from "@/contexts/data/InstitutionContext";
import InstitutionsContext from "@/contexts/data/InstitutionsContext";
import MasterGroupsContext from "@/contexts/data/MasterGroupsContext";
import TrialContext from "@/contexts/data/TrialContext";
import { faExclamationCircle } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, AlertTitle, Box, ListItemIcon, ListItemText, darken, lighten, styled } from "@mui/material";
import { DateTime } from "luxon";
import { FC, useCallback, useContext, useMemo } from "react";
import StatusList, { IStatusListCreateAdditional, IStatusListCreateConfigureLink, IStatusListCreateLabel, IStatusListCreateLink, IStatusListIsDisabled, IStatusListSearch, IStatusListStatsComponentProps, StatusListItemIcon, StatusListItemText, StatusListProps } from "./StatusList";
import { DefaultSortableFields, IStatusListSortable } from "./StatusListSort";
import DefaultStatComponents, { StatusListItemTooltip, StatusListItemTooltipStats, StatusListStatsBox } from './StatusListStats';
import ChosenThemeSizeContext from "@/contexts/styles/ChosenThemeSizeContext";


export interface IParticipantConfigurationListAdditional {
    trial?: Trial;
    masterGroup?: MasterGroup;
    coordinatingGroup?: CoordinatingGroup;
    institution?: Institution;
}

export interface IParticipantConfigurationListProps extends StatusListProps<Participant, IParticipantConfigurationListAdditional> {

}


const EnrolmentBox = styled(Box)(({ theme }) => ({ 
    display: 'flex',
    alignItems: 'center',
    width: theme.spacing(15),
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    background: theme.palette.mode === 'dark' ? darken(theme.palette.primary.main,0.5) : lighten(theme.palette.primary.main, 0.5) 
}));

const InstBox = styled(Box)(({ theme }) => ({ 
    display: 'flex',
    alignItems: 'center',
    width: theme.spacing(15),
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    background: theme.palette.mode === 'dark' ? darken(theme.palette.secondary.main,0.5) : lighten(theme.palette.secondary.main, 0.5)
}));

const createParticipantStatusLabel: IStatusListCreateLabel<Participant, IParticipantConfigurationListAdditional> = (itemData) => {
    return (
        <>
            <StatusListItemIcon>
                <FontAwesomeIcon fixedWidth size="lg" icon={AppSettings.icons.participant} />
            </StatusListItemIcon>
            <StatusListItemText 
                primary={itemData.data.studyNumber} 
            />
        </>
    )
};

const createParticipantLink: IStatusListCreateLink<Participant, IParticipantConfigurationListAdditional> = (itemData) => {
    if (itemData.additional.trial) {
        if (itemData.additional.institution) {
            return `/${itemData.additional.trial.oid}/configuration/institutions/${itemData.additional.institution.code}/participants/${itemData.data.studyNumber}`;
        }
    
        return `/${itemData.additional.trial.oid}/configuration/participants/${itemData.data.studyNumber}`
    }

    return `/configuration/participants/${itemData.data.studyNumber}`;
};

const createParticipantConfigurationLink: IStatusListCreateConfigureLink<Participant, IParticipantConfigurationListAdditional> = (itemData) => {
    return undefined;
};

const isParticipantDisabled: IStatusListIsDisabled<Participant, IParticipantConfigurationListAdditional> = (itemData) => {
    return !itemData.data.status || itemData.data.status === ConfigurationStatus.Disabled;
};

const isParticipantListItemDisabled: IStatusListIsDisabled<Participant, IParticipantConfigurationListAdditional> = (itemData) => {
    return false;
};

const ParticipantStatsComponent = ({
    itemData, 
    theme
}: IStatusListStatsComponentProps<Participant, IParticipantConfigurationListAdditional>) => {
    return (
        <StatusListStatsBox>
            <DefaultStatComponents.DisabledIndicatorStats itemData={itemData} theme={theme} />
            <DefaultStatComponents.ReadOnlyIndicatorStats itemData={itemData} theme={theme} />
            <StatusListItemTooltip
                placement="left"
                title={
                    <>
                        <strong>Enrolment Date</strong>
                        <Box>
                            {DateTime.fromISO(itemData.data.enrolmentDate!).toFormat('dd/MM/yyyy')}
                        </Box>
                    </>
                }
            >
                <EnrolmentBox>
                    {DateTime.fromISO(itemData.data.enrolmentDate!).toFormat('dd/MM/yyyy')}
                </EnrolmentBox>
            </StatusListItemTooltip>
            <StatusListItemTooltip
                placement="left"
                title={
                    <>
                        <strong>Institution Code</strong>
                        <StatusListItemTooltipStats>
                            <span>
                                Code
                            </span>
                            <span>
                                {itemData.additional.institution?.code}
                            </span>
                        </StatusListItemTooltipStats>
                        {
                            itemData.additional.masterGroup && (
                                <StatusListItemTooltipStats>
                                    <span>
                                        Master Group
                                    </span>
                                    <span>
                                        {itemData.additional.masterGroup.name}
                                    </span>
                                </StatusListItemTooltipStats>
                            )
                        }
                        {
                            itemData.additional.coordinatingGroup && (
                                <StatusListItemTooltipStats>
                                    <span>
                                        Coordinating Group
                                    </span>
                                    <span>
                                        {itemData.additional.coordinatingGroup.name}
                                    </span>
                                </StatusListItemTooltipStats>
                            )
                        }
                    </>
                }
            >
                <InstBox>
                    <FontAwesomeIcon size="lg" icon={AppSettings.icons.institution} /> {itemData.additional.institution?.code}
                </InstBox>
            </StatusListItemTooltip>
        </StatusListStatsBox>
    )
};


const SortableFields: IStatusListSortable<Participant, IParticipantConfigurationListAdditional>[] = [
    {
        name: 'Study Number',
        sort: (a, b) => a.data.studyNumber! > b.data.studyNumber! ? 1 : -1
    },
    {
        name: 'Enrolment Date',
        sort: (a, b) => a.data.enrolmentDate! > b.data.enrolmentDate! ? 1 : -1
    },
    {
        name: 'Institution Code',
        sort: (a, b) => a.additional.institution?.code! > b.additional.institution?.code! ? 1 : -1
    },
    DefaultSortableFields.status
]

const DefaultSearchData = { statuses: [ConfigurationStatus.Enabled, ConfigurationStatus.ReadOnly] };

const ParticipantStatusList: FC<IParticipantConfigurationListProps> = ({
    ...statusListProps
}) => {
    const { data: trial } = useContext(TrialContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: masterGroups, idMap: masterGroupMapping } = useContext(MasterGroupsContext);
    const { data: coordinatingGroups, idMap: coordinatingGroupMapping } = useContext(CoordinatingGroupsContext);
    const { data: institutions, idMap: institutionMapping } = useContext(InstitutionsContext);

    const createParticipantAdditional: IStatusListCreateAdditional<Participant, IParticipantConfigurationListAdditional> = useCallback((itemData) => {
        return {
            trial: trial,
            masterGroup: masterGroupMapping && itemData.data.masterGroupId ? masterGroupMapping[itemData.data.masterGroupId] : undefined,
            coordinatingGroup: coordinatingGroupMapping && itemData.data.coordinatingGroupId ? coordinatingGroupMapping[itemData.data.coordinatingGroupId] : undefined,
            institution: institutionMapping && itemData.data.institutionId ? institutionMapping[itemData.data.institutionId] : undefined
        }
    }, [trial, masterGroupMapping, coordinatingGroupMapping, institutionMapping]);

    

    const searchableFields: IStatusListSearch<Participant, IParticipantConfigurationListAdditional>[] = useMemo(() => {
        const output: IStatusListSearch<Participant, IParticipantConfigurationListAdditional, any, any>[] = [
            {
                key: 'studyNumber',
                component: Text,
                componentProps: {
                    margin: 'normal'
                } as ITextProps,
                label: 'Study Number',
                searchFn: (item, value) => value ? item.data.studyNumber?.includes(value) ?? false : false
            } as IStatusListSearch<Participant, IParticipantConfigurationListAdditional, string>
        ]

        if (!institution) {
            if (masterGroups && masterGroups.length > 0) {
                output.push({
                    key: 'masterGroups',
                    component: Multiselect,
                    componentProps: {
                        options: masterGroups?.map(x => ({
                            id: x.id,
                            name: x.name
                        })) ?? [],
                        FormControlProps: {
                            margin: 'normal'
                        }
                    } as IMultiselectProps,
                    label: 'Master Groups',
                    searchFn: (item, value) => value && value.length > 0 ?
                        item.data.masterGroupId ?
                            value.includes(item.data.masterGroupId) ?? false : 
                            false :
                        true
                } as IStatusListSearch<Participant, IParticipantConfigurationListAdditional, number[]>);
            }

            if (coordinatingGroups && coordinatingGroups.length > 0) {
                output.push({
                    key: 'coordinatingGroups',
                    component: Multiselect,
                    componentProps: {
                        options: coordinatingGroups?.map(x => ({
                            id: x.id,
                            name: x.name
                        })) ?? [],
                        optionsFilter: (options, formState) => {
                            if (!formState.value.masterGroups || formState.value.masterGroups.length === 0) {
                                return options;
                            }

                            return options.filter(x => coordinatingGroupMapping && formState.value.masterGroups.includes(coordinatingGroupMapping[x.id].masterGroupId))
                        },
                        FormControlProps: {
                            margin: 'normal'
                        }
                        
                    } as IMultiselectProps,
                    label: 'Coordinating Groups',
                    searchFn: (item, value) => value && value.length > 0 ?
                        item.data.coordinatingGroupId ?
                            value.includes(item.data.coordinatingGroupId) ?? false : 
                            false :
                        true
                } as IStatusListSearch<Participant, IParticipantConfigurationListAdditional, number[]>);
            }

            output.push({
                key: 'institutions',
                component: Multiselect,
                componentProps: {
                    options: institutions?.map(x => ({
                        id: x.id,
                        name: x.name
                    })) ?? [],
                    optionsFilter: (options, formState) => {
                        const hasMasterGroupFilter = !!formState.value.masterGroups && formState.value.masterGroups.length > 0;
                        const hasCoordinatingGroupFilter = !!formState.value.coordinatingGroups && formState.value.coordinatingGroups.length > 0

                        if (!hasMasterGroupFilter && !hasCoordinatingGroupFilter) {
                            return options;
                        }

                        return options.filter(x => {
                            if (!institutionMapping) {
                                return false;
                            }

                            if (hasMasterGroupFilter && !formState.value.masterGroups.includes(institutionMapping[x.id].masterGroupId)) {
                                return false;
                            }

                            if (hasCoordinatingGroupFilter && !formState.value.coordinatingGroups.includes(institutionMapping[x.id].coordinatingGroupId)) {
                                return false;
                            }

                            return true;
                        })
                    },
                    FormControlProps: {
                        margin: 'normal'
                    }
                    
                } as IMultiselectProps,
                label: 'Institutions',
                searchFn: (item, value) => value && value.length > 0 ?
                    item.data.institutionId ?
                        value.includes(item.data.institutionId) ?? false : 
                        false :
                    true
            } as IStatusListSearch<Participant, IParticipantConfigurationListAdditional, number[]>);
        }

        output.push({
            key: 'statuses',
            component: Multiselect,
            componentProps: {
                options: [
                    {
                        id: ConfigurationStatus.Disabled,
                        name: 'Disabled',
                        description: `The participant is disabled in the Source Data Verification system.`
                    },
                    {
                        id: ConfigurationStatus.ReadOnly,
                        name: 'Read Only',
                        description: `The participant is read only in the Source Data Verification system.`
                    },
                    {
                        id: ConfigurationStatus.Enabled,
                        name: 'Enabled',
                        description: `The participant is enabled in the Source Data Verification system.`
                    }
                ],
                FormControlProps: {
                    margin: 'normal'
                }
                
            } as IMultiselectProps,
            label: 'Statuses',
            searchFn: (item, value) => value && value.length > 0 ?
                item.data.status ?
                    value.includes(item.data.status) ?? false : 
                    false :
                true
        } as IStatusListSearch<Participant, IParticipantConfigurationListAdditional, ConfigurationStatus[]>)

        return output;
    }, [masterGroups, masterGroupMapping, coordinatingGroups, coordinatingGroupMapping, institutions, institutionMapping, institution]);
    

    const themeSize = useContext(ChosenThemeSizeContext);

    const height = useMemo(() => {
        switch (themeSize) {
            case 'extra-small':
                return 25;
            case 'small':
                return 37;
            case 'medium':
                return 49;
            case 'large':
                return 61;
            case 'extra-large':
                return 73;
        }
    }, [themeSize])

    return (
        <StatusList 
            {...statusListProps}
            createLabel={createParticipantStatusLabel}
            createLink={createParticipantLink}
            createConfigureLink={createParticipantConfigurationLink}
            isDisabled={isParticipantDisabled}
            isListItemDisabled={isParticipantListItemDisabled}
            createAdditional={createParticipantAdditional}
            StatsComponent={ParticipantStatsComponent}
            sortKey="configuration-participants-sort"
            sortableFields={SortableFields}
            noRecords={
                <Alert
                    severity="warning"
                >
                    <AlertTitle>No Participants</AlertTitle>
                    No participants were found on the {trial?.name} trial {institution ? `at ${institution.name}` : ''}.
                </Alert>
            }
            searchFields={searchableFields}
            searchKey={`configuration-participants-search-${trial?.oid}-${institution?.code ?? 'All'}`}
            fixedItemHeight={height}
            defaultSearchData={DefaultSearchData}
        />
    )
};

export default ParticipantStatusList;